/* eslint-disable max-lines */

import { MutableRefObject } from 'react';

import {
   HistoricalVisit,
   HistoricalVisitSimple,
   VisitDiagnosis,
   VisitDocumentFromTemplateSimple,
   VisitPrescription,
   VisitReferralSimple,
} from '@dreryk/egabinet-visit-frontend-api';

import {
   createSingleInterpolatedTranslationKey,
   createTranslatedText,
   dateFormatter,
   Translate,
} from '@dreryk/react-common';
import {
   appendTestableElementAttributes,
   ChevronRightIcon,
   IconSize,
   IconWrapper,
   SidePanelListItem,
   SidePanelListItemHeaderVariant,
   SidePanelListNoItemsMessage,
   SidePanelSlots,
   SidePanelTitledSectionsSection,
   Text,
   TextEditor,
   TextSeparator,
   TextSeparatorType,
} from '@dreryk/react-components';

import { isHistoricalVisitSimpleWithConfidentialDetails, VisitDiagnoses } from '~libs.shared.visit';

import { referralTranslationKeys } from '../../features/referral/translations';
import { historicalVisitsTranslationKeys, historicalVisitTranslationKeys } from '../../translations';
import { dispatchVisitDetailsCopyEvent } from '../../visit/form/VisitFormSynchronization.event';

import { HistoricalVisitConfidentialAccessButton } from './HistoricalVisitConfidentialAccessButton';
import { HistoricalVisitConfidentialAccessPendingButton } from './HistoricalVisitConfidentialAccessPendingButton';
import { HistoricalVisitDiagnosis } from './HistoricalVisitDiagnosis';
import { HistoricalVisitItemHeaderCopyIcon } from './HistoricalVisitItemHeaderCopyIcon';
import { HistoricalVisitLeave } from './HistoricalVisitLeave';
import { HistoricalVisitPrescriptionHeader } from './HistoricalVisitPrescriptionHeader';
import { HistoricalVisitPrescriptions } from './HistoricalVisitPrescriptions';
import { HistoricalVisitPrints } from './HistoricalVisitPrints';
import { CopyPrescriptionPackageAction } from './actions/CopyPrescriptionPackageAction';
import {
   StyledDiagnosis,
   StyledDiagnosisAddidionalInformationText,
   StyledDiagnosisContent,
} from './historicalVisits.styled';
import { HistoricalVisitPrintItemData } from './historicalVisits.types';
import { HistoricalVisitsListItemHeader } from './HistoricalVisitsListItemHeader';
import { HistoricalVisitsStatus, HistoricalVisitStatus } from './HistoricalVisitsStatus';
import { HistoricalVisitElectronicDocuments } from './HistoricalVisitElectronicDocuments';

// eslint-disable-next-line max-lines-per-function
export const createHistoricalVisitsListItems = (
   historicalVisits: HistoricalVisitSimple[],
   onItemClick: (visitId: number) => void
): SidePanelListItem[] =>
   historicalVisits.map(
      // eslint-disable-next-line max-lines-per-function
      (historicalVisit, idx: number): SidePanelListItem => {
         if (!isHistoricalVisitSimpleWithConfidentialDetails(historicalVisit)) {
            return {
               id: historicalVisit.visitId,
               headerVariant: SidePanelListItemHeaderVariant.PRIMARY,
               headerContent: (
                  <HistoricalVisitsListItemHeader
                     text={createTranslatedText(dateFormatter(historicalVisit.date))}
                     {...appendTestableElementAttributes('expand')}
                  >
                     {!historicalVisit.approved && (
                        <HistoricalVisitsStatus visitStatus={HistoricalVisitStatus.NOT_APPROVED} />
                     )}
                  </HistoricalVisitsListItemHeader>
               ),
               content: historicalVisit.pendingVisitConfidentialAccess ? (
                  <HistoricalVisitConfidentialAccessPendingButton />
               ) : (
                  <HistoricalVisitConfidentialAccessButton visitId={historicalVisit.visitId} />
               ),
            };
         }

         const contentSections: SidePanelTitledSectionsSection[] = [
            {
               title: createSingleInterpolatedTranslationKey(
                  'visit:sidePanel:historicalVisits:diagnosis',
                  'count',
                  historicalVisit.confidentialDetails.diagnoses.length
               ),
               children: historicalVisit.confidentialDetails.diagnoses.map((diagnosis) => (
                  <StyledDiagnosis
                     key={diagnosis.id}
                     $main={diagnosis.main}
                     {...appendTestableElementAttributes(diagnosis.icd10Code)}
                  >
                     <Text testId={'code'}>{diagnosis.icd10Code}</Text>
                     <StyledDiagnosisContent>
                        <TextSeparator separator={TextSeparatorType.SPACE}>
                           <Text testId={'name'}>{diagnosis.name}</Text>
                           {diagnosis.chronic ? (
                              <Text testId={'chronic'}>
                                 <Translate>{historicalVisitsTranslationKeys.historicalVisit.chronic}</Translate>
                              </Text>
                           ) : null}
                        </TextSeparator>
                        {diagnosis.additionalInformation ? (
                           <StyledDiagnosisAddidionalInformationText testId={'additionalInformation'} maxLines={1}>
                              {diagnosis.additionalInformation}
                           </StyledDiagnosisAddidionalInformationText>
                        ) : null}
                     </StyledDiagnosisContent>
                  </StyledDiagnosis>
               )),
            },
         ];
         if (idx === 0) {
            contentSections.push({
               title: createSingleInterpolatedTranslationKey(
                  'visit:sidePanel:historicalVisits:prescribedMedicines',
                  'count',
                  historicalVisit.confidentialDetails.prescriptions?.length || 0
               ),
               children: (historicalVisit.confidentialDetails.prescriptions || []).map((prescription) => (
                  <HistoricalVisitPrescriptionHeader
                     key={prescription.id}
                     name={prescription.name}
                     form={prescription.form}
                     packageContent={prescription.packageContent}
                  />
               )),
            });
         }
         return {
            id: historicalVisit.visitId,
            headerVariant: SidePanelListItemHeaderVariant.PRIMARY,
            headerContent: (
               <HistoricalVisitsListItemHeader
                  text={createTranslatedText(dateFormatter(historicalVisit.date))}
                  {...appendTestableElementAttributes('expand')}
                  icon={
                     <IconWrapper testId={'expand'} size={IconSize.SMALL}>
                        <ChevronRightIcon />
                     </IconWrapper>
                  }
               >
                  {!historicalVisit.approved && (
                     <HistoricalVisitsStatus visitStatus={HistoricalVisitStatus.NOT_APPROVED} />
                  )}
               </HistoricalVisitsListItemHeader>
            ),
            onClick: () => {
               onItemClick(historicalVisit.visitId);
            },
            content: <SidePanelSlots.TitledSections sections={contentSections} />,
         };
      }
   );

const renderHistoricalVisitDiagnoses = (diagnoses: VisitDiagnosis[]): JSX.Element[] =>
   diagnoses.map((diagnosis) => (
      <StyledDiagnosis
         key={diagnosis.id}
         $main={diagnosis.main}
         {...appendTestableElementAttributes(diagnosis.icd10Code)}
      >
         <Text testId={'code'}>{diagnosis.icd10Code}</Text>
         <StyledDiagnosisContent>
            <TextSeparator separator={TextSeparatorType.SPACE}>
               <Text testId={'name'}>{diagnosis.name}</Text>
               {diagnosis.chronic ? (
                  <Text testId={'chronic'}>
                     <Translate>{historicalVisitsTranslationKeys.historicalVisit.chronic}</Translate>
                  </Text>
               ) : null}
            </TextSeparator>
            {diagnosis.additionalInformation ? (
               <StyledDiagnosisAddidionalInformationText testId={'additionalInformation'}>
                  {diagnosis.additionalInformation}
               </StyledDiagnosisAddidionalInformationText>
            ) : null}
         </StyledDiagnosisContent>
      </StyledDiagnosis>
   ));

const mapReferralsToPrintItems = (referrals: VisitReferralSimple[]): HistoricalVisitPrintItemData[] => {
   return referrals.map((referral) => ({
      label: referralTranslationKeys.referralType[referral.referralType],
   }));
};

const mapMedicalCertificatesToPrintItems = (visitMedicalCertificateIds: number[]): HistoricalVisitPrintItemData[] => {
   return visitMedicalCertificateIds.map(() => ({
      label: historicalVisitsTranslationKeys.historicalVisit.medicalCertificate,
   }));
};

const mapDocumentTemplatesToPrintItems = (
   documentFromTemplateList: VisitDocumentFromTemplateSimple[]
): HistoricalVisitPrintItemData[] => {
   return documentFromTemplateList.map((documentTemplate) => ({
      label: createTranslatedText(documentTemplate.templateName),
   }));
};

const getPrescriptionsCount = (prescriptions: VisitPrescription[]): number =>
   prescriptions.filter((prescription) => !prescription.annulled).length;

const getPrintItems = (historicalVisit: HistoricalVisit): HistoricalVisitPrintItemData[] =>
   [
      mapReferralsToPrintItems(historicalVisit.referrals),
      mapMedicalCertificatesToPrintItems(historicalVisit.visitMedicalCertificateIds),
      mapDocumentTemplatesToPrintItems(historicalVisit.documentFromTemplateList),
   ].flat();

enum HistoricalVisitItemId {
   DIAGNOSES = 'diagnoses',
   INTERVIEW = 'interview',
   SUBJECTIVE_ASSESSMENT = 'subjectiveAssessment',
   RECOMMENDATIONS = 'recommendations',
   PRESCRIPTIONS = 'prescriptions',
   EDOCUMENTS = 'eDokumenty',
   MEDICAL_LEAVES = 'medicalLeaves',
   PRINTS = 'prints',
}

// eslint-disable-next-line max-lines-per-function
export const createHistoricalVisitItems = (
   historicalVisit: HistoricalVisit | undefined,
   visitFormCopyAvailable: boolean,
   targetVisitId: number,
   patientId: number,
   visitDiagnoses: VisitDiagnoses
): SidePanelListItem[] | undefined => {
   if (!historicalVisit) {
      return undefined;
   }
   const prints = getPrintItems(historicalVisit);
   const electronicDocumentsCount = historicalVisit.electronicReferrals.length + historicalVisit.dischareSummariesCount;
   const prescriptionsBackdropElementRef: MutableRefObject<HTMLElement | null> = { current: null };

   return [
      {
         id: HistoricalVisitItemId.DIAGNOSES,
         headerContent: (
            <HistoricalVisitsListItemHeader
               text={createSingleInterpolatedTranslationKey(
                  historicalVisitTranslationKeys.diagnoses,
                  'count',
                  historicalVisit.diagnoses.length
               )}
               icon={
                  <HistoricalVisitItemHeaderCopyIcon
                     tooltipText={historicalVisitTranslationKeys.copyIcons.diagnoses}
                     onClick={() =>
                        dispatchVisitDetailsCopyEvent({
                           diagnoses: {
                              behavior: 'append',
                              value: historicalVisit.diagnoses.map((diangosis) => ({
                                 code: diangosis.icd10Code,
                                 name: diangosis.name,
                                 additionalInformation: diangosis.additionalInformation,
                                 main: diangosis.main,
                              })),
                           },
                        })
                     }
                     visible={visitFormCopyAvailable}
                  />
               }
            />
         ),
         content: (
            <HistoricalVisitDiagnosis>
               {renderHistoricalVisitDiagnoses(historicalVisit.diagnoses)}
            </HistoricalVisitDiagnosis>
         ),
      },
      {
         id: HistoricalVisitItemId.INTERVIEW,
         headerContent: (
            <HistoricalVisitsListItemHeader
               text={historicalVisitTranslationKeys.interview}
               icon={
                  <HistoricalVisitItemHeaderCopyIcon
                     tooltipText={historicalVisitTranslationKeys.copyIcons.interview}
                     onClick={() => dispatchVisitDetailsCopyEvent({ interview: historicalVisit.interview })}
                     visible={visitFormCopyAvailable ? Boolean(historicalVisit.interview) : false}
                  />
               }
            />
         ),
         content: (
            <SidePanelSlots.Expandable>
               <TextEditor.SafeHtmlRenderer>{historicalVisit.interview}</TextEditor.SafeHtmlRenderer>
            </SidePanelSlots.Expandable>
         ),
      },
      {
         id: HistoricalVisitItemId.SUBJECTIVE_ASSESSMENT,
         headerContent: (
            <HistoricalVisitsListItemHeader
               text={historicalVisitTranslationKeys.subjectiveAssessment}
               icon={
                  <HistoricalVisitItemHeaderCopyIcon
                     tooltipText={historicalVisitTranslationKeys.copyIcons.subjectiveAssessment}
                     onClick={() =>
                        dispatchVisitDetailsCopyEvent({ subjectiveAssessment: historicalVisit.physicalExamination })
                     }
                     visible={visitFormCopyAvailable ? Boolean(historicalVisit.physicalExamination) : false}
                  />
               }
            />
         ),
         content: (
            <SidePanelSlots.Expandable>
               <TextEditor.SafeHtmlRenderer>{historicalVisit.physicalExamination}</TextEditor.SafeHtmlRenderer>
            </SidePanelSlots.Expandable>
         ),
      },
      {
         id: HistoricalVisitItemId.RECOMMENDATIONS,
         headerContent: (
            <HistoricalVisitsListItemHeader
               text={historicalVisitTranslationKeys.recommendations}
               icon={
                  <HistoricalVisitItemHeaderCopyIcon
                     tooltipText={historicalVisitTranslationKeys.copyIcons.recommendations}
                     onClick={() => dispatchVisitDetailsCopyEvent({ recommendations: historicalVisit.recommendations })}
                     visible={visitFormCopyAvailable ? Boolean(historicalVisit.recommendations) : false}
                  />
               }
            />
         ),
         content: (
            <SidePanelSlots.Expandable>
               <TextEditor.SafeHtmlRenderer>{historicalVisit.recommendations}</TextEditor.SafeHtmlRenderer>
            </SidePanelSlots.Expandable>
         ),
      },
      {
         id: HistoricalVisitItemId.PRESCRIPTIONS,
         headerContent: (
            <HistoricalVisitsListItemHeader
               text={createSingleInterpolatedTranslationKey(
                  historicalVisitTranslationKeys.prescriptions,
                  'count',
                  getPrescriptionsCount(historicalVisit.prescriptions)
               )}
               icon={
                  <CopyPrescriptionPackageAction
                     prescriptionIds={historicalVisit.prescriptions.map((prescription) => prescription.prescriptionId)}
                     targetVisitId={targetVisitId}
                     patientId={patientId}
                     visitDiagnoses={visitDiagnoses}
                  >
                     {({ copyPrescriptionPackage }) => (
                        <HistoricalVisitItemHeaderCopyIcon
                           tooltipText={historicalVisitTranslationKeys.copyIcons.prescriptions}
                           onClick={copyPrescriptionPackage}
                           visible={
                              historicalVisit.prescriptions.length > 0 &&
                              !historicalVisit.prescriptions.some((prescription) => prescription.annulled)
                           }
                           backdropElementRef={prescriptionsBackdropElementRef}
                        />
                     )}
                  </CopyPrescriptionPackageAction>
               }
            />
         ),
         content: (
            <HistoricalVisitPrescriptions
               prescriptions={historicalVisit.prescriptions}
               copyingAvailable={visitFormCopyAvailable}
               targetVisitId={targetVisitId}
               patientId={patientId}
               visitDiagnoses={visitDiagnoses}
               backdropElementRef={prescriptionsBackdropElementRef}
            />
         ),
      },
      {
         id: HistoricalVisitItemId.EDOCUMENTS,
         headerContent: (
            <HistoricalVisitsListItemHeader
               text={createSingleInterpolatedTranslationKey(
                  historicalVisitTranslationKeys.electronicDocuments,
                  'count',
                  electronicDocumentsCount
               )}
            />
         ),
         content: <HistoricalVisitElectronicDocuments historicalVisit={historicalVisit} />,
      },
      {
         id: HistoricalVisitItemId.MEDICAL_LEAVES,
         headerContent: (
            <HistoricalVisitsListItemHeader
               text={createSingleInterpolatedTranslationKey(
                  historicalVisitTranslationKeys.medicalLeaves,
                  'count',
                  historicalVisit.medicalLeaves.length
               )}
            />
         ),
         content:
            historicalVisit.medicalLeaves.length > 0 ? (
               historicalVisit.medicalLeaves.map((medicalLeave) => {
                  return (
                     <HistoricalVisitLeave
                        key={medicalLeave.id}
                        dateFrom={medicalLeave.dateFrom}
                        dateTo={medicalLeave.dateTo}
                     />
                  );
               })
            ) : (
               <SidePanelListNoItemsMessage message={historicalVisitTranslationKeys.noLeavesIssued} />
            ),
      },
      {
         id: HistoricalVisitItemId.PRINTS,
         headerContent: (
            <HistoricalVisitsListItemHeader
               text={createSingleInterpolatedTranslationKey(
                  historicalVisitTranslationKeys.prints,
                  'count',
                  prints.length
               )}
            />
         ),
         content: <HistoricalVisitPrints prints={prints} />,
      },
   ];
};
